import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import panelApi from "@/lib/panelApi";
import { numberFieldProps, dateFieldProps } from "@/lib/numberInput";
import { DatePicker } from "@/components/DatePicker";
import { NumberInput } from "@/components/NumberInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { FileEdit, Send, AlertTriangle } from "lucide-react";
import CapacityMismatchModal from "./CapacityMismatchModal";
import HotelCombobox from "./HotelCombobox";

/**
 * ChangeRequestModal — acente-facing edit surface.
 *
 * Instead of directly mutating the reservation (which is now blocked for
 * agencies), the acente opens this modal, changes the fields they want, and
 * writes a note. On submit we POST to /panel/revisions with a
 * `proposed_changes` diff of ONLY the fields that actually changed. Admin/ops
 * see the diff in Revizyon Talepleri and approve.
 */
const MEAL_OPTIONS = [
  { v: "kahvalti", t: "Kahvaltı" },
  { v: "aksam", t: "Akşam" },
];

// Shared UX helpers moved to /app/frontend/src/lib/numberInput.js — see
// that file for the rationale. Importing keeps the fix in one place.

function initFromReservation(r) {
  return {
    group_name: r.group_name || "",
    hotel_name: r.hotel_name || "",
    checkin: r.checkin || "",
    checkout: r.checkout || "",
    adults: r.adults ?? 0,
    children: r.children ?? 0,
    staff: r.staff ?? 0,
    rooms: { r1: r.rooms?.r1 ?? 0, r2: r.rooms?.r2 ?? 0, r3: r.rooms?.r3 ?? 0, r4: r.rooms?.r4 ?? 0, r5: r.rooms?.r5 ?? 0 },
    meal_start: r.meal_start || "kahvalti",
    meal_end: r.meal_end || "aksam",
    notes: r.notes || "",
  };
}

export default function ChangeRequestModal({ open, onOpenChange, reservation, onSubmitted }) {
  const [form, setForm] = useState(() => initFromReservation(reservation || {}));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [capacityModalOpen, setCapacityModalOpen] = useState(false);

  // When modal opens for a new reservation, sync form state.
  useEffect(() => {
    if (open && reservation) {
      setForm(initFromReservation(reservation));
      setNote("");
    }
  }, [open, reservation]);

  const original = useMemo(() => initFromReservation(reservation || {}), [reservation]);

  // Compute only-changed fields → posted as `proposed_changes` on the revision.
  const diff = useMemo(() => {
    const out = {};
    const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
    for (const k of ["group_name", "hotel_name", "checkin", "checkout", "adults", "children", "staff", "meal_start", "meal_end", "notes"]) {
      if (form[k] !== original[k]) out[k] = typeof form[k] === "number" ? form[k] : form[k];
    }
    if (!same(form.rooms, original.rooms)) out.rooms = { ...form.rooms };
    return out;
  }, [form, original]);
  const hasChanges = Object.keys(diff).length > 0;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNum = (k, v) => setField(k, v === "" ? 0 : Math.max(0, parseInt(v || "0", 10)));
  const setRoom = (k, v) => setForm((f) => ({ ...f, rooms: { ...f.rooms, [k]: Math.max(0, parseInt(v || "0", 10)) } }));

  // Live capacity check — same convention as the main form.
  const totalRooms = (form.rooms.r1 || 0) + (form.rooms.r2 || 0) + (form.rooms.r3 || 0) + (form.rooms.r4 || 0) + (form.rooms.r5 || 0);
  const roomCapacity = 1 * (form.rooms.r1 || 0) + 2 * (form.rooms.r2 || 0) + 3 * (form.rooms.r3 || 0) + 4 * (form.rooms.r4 || 0) + 5 * (form.rooms.r5 || 0);
  const totalPeople = (form.adults || 0) + (form.children || 0) + (form.staff || 0);
  const capacityDiff = roomCapacity - totalPeople;
  const capacityWarn = totalRooms > 0 && totalPeople > 0 && capacityDiff !== 0;

  const doPersist = async () => {
    setCapacityModalOpen(false);
    setBusy(true);
    try {
      await panelApi.post("/revisions", {
        reservation_id: reservation.id,
        note: note.trim(),
        proposed_changes: {
          ...diff,
          ...(diff.rooms ? { rooms: form.rooms } : {}),
        },
      });
      toast.success("Değişiklik talebiniz alındı — onaylandığında rezervasyona uygulanacak", { duration: 6000 });
      onOpenChange(false);
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Talep gönderilemedi");
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!hasChanges) return toast.error("Herhangi bir alan değiştirmediniz");
    if (note.trim().length < 2) return toast.error("Kısa bir not / neden yazın (en az 2 karakter)");
    if (form.checkout && form.checkin && form.checkout < form.checkin) {
      return toast.error("Çıkış tarihi girişten önce olamaz");
    }
    // Capacity gate — block until user acknowledges mismatch
    if (capacityWarn) {
      setCapacityModalOpen(true);
      return;
    }
    await doPersist();
  };

  if (!reservation) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="change-request-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-brand-navy flex items-center gap-2">
            <FileEdit size={18} /> Değişiklik Yap
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-600">
            Değiştirmek istediğiniz alanları güncelleyin ve alt kısımda kısaca değişikliğin nedenini yazın. Talebiniz operasyon ekibi tarafından onaylandıktan sonra rezervasyona yansır.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label>Otel Adı</Label>
              <HotelCombobox
                value={form.hotel_name}
                onChange={(v) => setField("hotel_name", v)}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Grup Adı</Label>
              <Input value={form.group_name} onChange={(e) => setField("group_name", e.target.value)} data-testid="cr-group-name" />
            </div>
            <div>
              <Label>Giriş Tarihi</Label>
              <DatePicker value={form.checkin} onChange={(v) => setField("checkin", v)} placeholder="Giriş tarihi seçin" data-testid="cr-checkin" />
            </div>
            <div>
              <Label>Çıkış Tarihi</Label>
              <DatePicker value={form.checkout} onChange={(v) => setField("checkout", v)} minDate={form.checkin || undefined} placeholder="Çıkış tarihi seçin" data-testid="cr-checkout" />
            </div>
          </div>

          <div className="grid gap-3 grid-cols-3">
            <div>
              <Label>Yetişkin</Label>
              <NumberInput min={0} value={form.adults} onChange={(v) => setForm((f) => ({ ...f, adults: Math.max(0, v || 0) }))} data-testid="cr-adults" />
            </div>
            <div>
              <Label>Çocuk</Label>
              <NumberInput min={0} value={form.children} onChange={(v) => setForm((f) => ({ ...f, children: Math.max(0, v || 0) }))} data-testid="cr-children" />
            </div>
            <div>
              <Label>Görevli</Label>
              <NumberInput min={0} value={form.staff} onChange={(v) => setForm((f) => ({ ...f, staff: Math.max(0, v || 0) }))} data-testid="cr-staff" />
            </div>
          </div>

          <div>
            <Label className="mb-1 block">Oda Dağılımı</Label>
            <div className="grid grid-cols-5 gap-2">
              {["r1", "r2", "r3", "r4", "r5"].map((k, i) => (
                <div key={k}>
                  <div className="text-[10px] uppercase text-slate-500">{i + 1} kişilik</div>
                  <NumberInput min={0} value={form.rooms[k]} onChange={(v) => setForm((f) => ({ ...f, rooms: { ...f.rooms, [k]: Math.max(0, v || 0) } }))} data-testid={`cr-room-${k}`} />
                </div>
              ))}
            </div>
            {/* Capacity inline hint */}
            {(totalRooms > 0 || totalPeople > 0) && (
              <div className={`mt-2 text-[11px] rounded-lg px-2.5 py-1.5 inline-flex items-center gap-2 ${
                capacityDiff === 0 && totalRooms > 0 && totalPeople > 0
                  ? "bg-emerald-100 text-emerald-800"
                  : capacityWarn
                    ? (capacityDiff > 0 ? "bg-amber-100 text-amber-900" : "bg-rose-100 text-rose-900")
                    : "bg-slate-100 text-slate-600"
              }`} data-testid="cr-capacity-hint">
                Kapasite <b>{roomCapacity}</b> / Kişi <b>{totalPeople}</b>
                {capacityWarn && (capacityDiff > 0 ? ` — ${capacityDiff} kişilik boşluk` : ` — ${Math.abs(capacityDiff)} kişilik eksik`)}
                {capacityDiff === 0 && totalRooms > 0 && totalPeople > 0 && " — uyumlu"}
              </div>
            )}
          </div>

          <div className="grid gap-3 grid-cols-2">
            <div>
              <Label>Başlangıç Öğünü</Label>
              <select value={form.meal_start} onChange={(e) => setField("meal_start", e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" data-testid="cr-meal-start">
                {MEAL_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
                {form.meal_start === "ogle" && <option value="ogle">Öğle (eski)</option>}
              </select>
            </div>
            <div>
              <Label>Bitiş Öğünü</Label>
              <select value={form.meal_end} onChange={(e) => setField("meal_end", e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" data-testid="cr-meal-end">
                {MEAL_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
                {form.meal_end === "ogle" && <option value="ogle">Öğle (eski)</option>}
              </select>
            </div>
          </div>

          <div>
            <Label>Not (rezervasyona ilişkin)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setField("notes", e.target.value)} data-testid="cr-notes" />
          </div>

          {/* Diff summary */}
          <div
            className={`rounded-xl p-3 text-xs ${hasChanges ? "bg-brand-orange/10 border border-brand-orange/30" : "bg-slate-100 text-slate-500"}`}
            data-testid="cr-diff-summary"
          >
            {hasChanges ? (
              <>
                <div className="font-semibold text-brand-orange-dark uppercase tracking-widest mb-1">Değişecek Alanlar ({Object.keys(diff).length})</div>
                <div className="flex flex-wrap gap-1.5 text-brand-navy">
                  {Object.keys(diff).map((k) => (
                    <span key={k} className="rounded-full bg-white/70 border border-brand-orange/30 px-2 py-0.5">{k}</span>
                  ))}
                </div>
              </>
            ) : (
              <span className="inline-flex items-center gap-1"><AlertTriangle size={12} /> Henüz bir değişiklik yapmadınız.</span>
            )}
          </div>

          <div>
            <Label>Değişiklik Nedeni / Açıklama *</Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Örn: giriş tarihini 1 gün öteleyin, yetişkin sayısı 3 arttı..."
              required
              data-testid="cr-reason"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Vazgeç</Button>
            <Button
              type="submit"
              disabled={busy || !hasChanges}
              className="bg-brand-orange text-white hover:bg-brand-orange-dark disabled:opacity-60"
              data-testid="cr-submit"
            >
              <Send size={14} className="mr-2" /> {busy ? "Gönderiliyor..." : "Değişiklik Talebini Gönder"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <CapacityMismatchModal
        open={capacityModalOpen}
        onCancel={() => setCapacityModalOpen(false)}
        onConfirm={doPersist}
        roomCapacity={roomCapacity}
        totalPeople={totalPeople}
        adults={form.adults}
        children={form.children}
        staff={form.staff}
        rooms={form.rooms}
      />
    </Dialog>
  );
}
